import { useState, useMemo, useEffect, memo } from "react";
import { useNavigate } from "react-router";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { getInvoiceStatus, getInvoiceStatusColor, getAssignedStatus, Invoice, normalizeCollectionRoute, type InvoiceStatus } from "../../types/invoice";
import InvoiceDetailsDrawer from "../../components/invoices/InvoiceDetailsDrawer";
import InvoiceSettingsModal from "../../components/invoices/InvoiceSettingsModal";
import InvoiceDashboardEmailSettings from "../../components/invoices/InvoiceDashboardEmailSettings";
import { HeroKPICard } from "../manage-revenueMIS/HeroKPICard";
import { SearchableSelect } from "../../components/ui/searchable-select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { format } from "date-fns";
import {
  Eye, FileText, CreditCard, Mail, Download,
  Receipt, TrendingUp, TrendingDown, CheckCircle, AlertTriangle, Clock, IndianRupee,
  ChevronRight, ChevronLeft, FileX, QrCode, Loader2,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";
import apiService from "../../services/apiService";
import { invoiceTableData } from "../../types/invoice";
import astrafinLogo from "../../assets/logo_two.svg";
import { getFinancialYear } from "../../lib/enhanced-currency-utils";

type InvoiceExportType = "invoice" | "payment";

type ExportColumn = { key: string; label: string; alts?: string[] };

const INVOICE_EXPORT_COLUMNS: ExportColumn[] = [
  { key: "astrafinGstin", label: "Astrafin GSTIN", alts: ["astrafin_gstin"] },
  { key: "companyName", label: "Company Name", alts: ["company_name", "customerName"] },
  { key: "customerGstin", label: "Customer GSTIN (Bill To)", alts: ["customer_gstin", "billToGstin"] },
  { key: "rsNo", label: "RS No.", alts: ["rs_no", "rentalScheduleDisplayId"] },
  { key: "installmentNo", label: "Installment No.", alts: ["installment_no", "periodNo"] },
  { key: "invoiceDate", label: "Invoice Date", alts: ["invoice_date"] },
  { key: "dueDate", label: "Due Date", alts: ["due_date"] },
  { key: "baseRental", label: "Base Rental", alts: ["base_rental"] },
  { key: "cgst", label: "CGST", alts: ["cgstAmount"] },
  { key: "sgst", label: "SGST", alts: ["sgstAmount"] },
  { key: "igst", label: "IGST", alts: ["igstAmount"] },
  { key: "tds", label: "TDS", alts: ["tdsRounded"] },
  { key: "totalRental", label: "Total Rental", alts: ["total_rental", "grandTotalRounded"] },
  { key: "eInvoiceStatus", label: "E-Invoice Status", alts: ["e_invoice_status", "externalInvoiceStatus"] },
  { key: "ackDate", label: "Ack Date", alts: ["ack_date", "externalAckDate"] },
];

const PAYMENT_EXPORT_COLUMNS: ExportColumn[] = [
  { key: "companyName", label: "Company Name", alts: ["company_name", "customerName"] },
  { key: "rsNo", label: "RS No.", alts: ["rs_no", "rentalScheduleDisplayId"] },
  { key: "installmentNo", label: "Installment No.", alts: ["installment_no", "periodNo"] },
  { key: "assignedLender", label: "Assigned Lender", alts: ["assigned_lender", "lenderName"] },
  { key: "nachStatus", label: "Nach Status", alts: ["nach_status", "nach"] },
  { key: "invoiceDate", label: "Invoice Date", alts: ["invoice_date"] },
  { key: "dueDate", label: "Due Date", alts: ["due_date"] },
  { key: "baseRental", label: "Base Rental", alts: ["base_rental"] },
  { key: "gst", label: "GST", alts: ["gstRounded"] },
  { key: "tds", label: "TDS", alts: ["tdsRounded"] },
  { key: "totalRental", label: "Total Rental", alts: ["total_rental", "grandTotalRounded"] },
  { key: "caseType", label: "Case Type", alts: ["case_type", "collectionRoute"] },
  { key: "assignmentStatus", label: "Assignment Status", alts: ["assignment_status", "assignedStatus"] },
  { key: "toLender", label: "To Lender", alts: ["to_lender", "amountPayableToLender"] },
  { key: "lenderPaymentStatus", label: "Lender Payment Status", alts: ["lender_payment_status"] },
  { key: "toAstrafin", label: "To Astrafin", alts: ["to_astrafin", "amountPayableToAstrafin"] },
  { key: "astrafinPaymentStatus", label: "Astrafin Payment Status", alts: ["astrafin_payment_status"] },
  { key: "astrafinPaymentDate", label: "Astrafin Payment Date", alts: ["astrafin_payment_date"] },

];

function getDefaultInvoiceExportDates() {
  const fy = getFinancialYear();
  const today = format(new Date(), "yyyy-MM-dd");
  return {
    fromDate: fy.start,
    toDate: today < fy.end ? today : fy.end,
  };
}

function pickExportValue(row: Record<string, unknown>, column: ExportColumn): unknown {
  const keys = [column.key, ...(column.alts ?? [])];
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") return row[key];
  }
  return "";
}

function pickExportRows(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response as Record<string, unknown>[];
  const root = response as Record<string, unknown> | null;
  const data = root?.data;
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === "object") {
    const nested = data as Record<string, unknown>;
    if (Array.isArray(nested.rows)) return nested.rows as Record<string, unknown>[];
    if (Array.isArray(nested.invoices)) return nested.invoices as Record<string, unknown>[];
    if (Array.isArray(nested.payments)) return nested.payments as Record<string, unknown>[];
  }
  if (Array.isArray(root?.rows)) return root.rows as Record<string, unknown>[];
  return [];
}

const EXPORT_FILL_GREEN = "FFDCFCE7";
const EXPORT_FILL_RED = "FFFEE2E2";
const EXPORT_FONT_GREEN = "FF166534";
const EXPORT_FONT_RED = "FF991B1B";

function getExportStatusStyle(columnKey: string, value: unknown): { fill: string; font: string } | null {
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return null;

  if (columnKey === "lenderPaymentStatus" || columnKey === "astrafinPaymentStatus") {
    if (text.includes("unpaid")) return { fill: EXPORT_FILL_RED, font: EXPORT_FONT_RED };
    if (text.includes("paid")) return { fill: EXPORT_FILL_GREEN, font: EXPORT_FONT_GREEN };
    return null;
  }

  if (columnKey === "eInvoiceStatus") {
    if (text.includes("cancel") || text.includes("fail")) {
      return { fill: EXPORT_FILL_RED, font: EXPORT_FONT_RED };
    }
    if (text.includes("generated") || text.includes("irn generated")) {
      return { fill: EXPORT_FILL_GREEN, font: EXPORT_FONT_GREEN };
    }
    return null;
  }

  return null;
}

async function downloadExportExcel(
  rows: Record<string, unknown>[],
  columns: ExportColumn[],
  fileName: string,
) {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet("Export");
  const center = { horizontal: "center" as const, vertical: "middle" as const };

  worksheet.columns = columns.map((c) => ({
    width: Math.max(16, c.label.length + 2),
    style: { alignment: center },
  }));

  const headerRow = worksheet.addRow(columns.map((c) => c.label));
  headerRow.font = { bold: true };
  headerRow.alignment = center;
  headerRow.eachCell((cell) => {
    cell.alignment = center;
    cell.font = { bold: true };
  });

  for (const row of rows) {
    const excelRow = worksheet.addRow(columns.map((c) => pickExportValue(row, c)));
    excelRow.alignment = center;
    excelRow.eachCell((cell, colNumber) => {
      cell.alignment = center;
      const column = columns[colNumber - 1];
      const statusStyle = column ? getExportStatusStyle(column.key, cell.value) : null;
      if (statusStyle) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: statusStyle.fill } };
        cell.font = { color: { argb: statusStyle.font } };
      }
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

const InvoiceExportButton = memo(function InvoiceExportButton() {
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exportType, setExportType] = useState<InvoiceExportType>("invoice");
  const [exporting, setExporting] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      const defaults = getDefaultInvoiceExportDates();
      setFromDate(defaults.fromDate);
      setToDate(defaults.toDate);
      setExportType("invoice");
    }
    setOpen(nextOpen);
  };

  const handleExport = async () => {
    if (!fromDate || !toDate) {
      toast({ title: "Missing dates", description: "Please select From Date and To Date", variant: "destructive" });
      return;
    }
    if (fromDate > toDate) {
      toast({ title: "Invalid range", description: "From Date cannot be after To Date", variant: "destructive" });
      return;
    }
    if (exporting) return;

    setExporting(true);
    try {
      const response = await apiService.post<unknown>(
        "/rental-schedule/export-rental-invoice-report",
        { fromDate, toDate, exportType }
      );
      const rows = pickExportRows(response);
      if (!rows.length) {
        toast({
          title: "No data",
          description: "No records found for the selected date range.",
          variant: "destructive",
        });
        return;
      }

      const columns = exportType === "invoice" ? INVOICE_EXPORT_COLUMNS : PAYMENT_EXPORT_COLUMNS;
      await downloadExportExcel(rows, columns, `${exportType}-report-${fromDate}-to-${toDate}.xlsx`);
      toast({
        title: "Exported",
        description: `${exportType === "invoice" ? "Invoice" : "Payment"} report downloaded (${rows.length} rows).`,
      });
      setOpen(false);
    } catch (error) {
      console.error("Error exporting invoice/payment report:", error);
      toast({ title: "Error", description: "Failed to export report", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => handleOpenChange(true)}>
        <Download className="h-4 w-4 mr-1" />
        Export
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Export Invoice Report
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="invoice-export-from-date">From Date</Label>
                <Input
                  id="invoice-export-from-date"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invoice-export-to-date">To Date</Label>
                <Input
                  id="invoice-export-to-date"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Export Type</Label>
              <RadioGroup
                value={exportType}
                onValueChange={(v) => setExportType(v as InvoiceExportType)}
                className="grid grid-cols-2 gap-3"
              >
                <label
                  htmlFor="export-type-invoice"
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    exportType === "invoice"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  }`}
                >
                  <RadioGroupItem value="invoice" id="export-type-invoice" />
                  <div>
                    <span className="text-sm font-medium">Invoice</span>
                    <p className="text-xs text-muted-foreground">GST / e-invoice details</p>
                  </div>
                </label>
                <label
                  htmlFor="export-type-payment"
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    exportType === "payment"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  }`}
                >
                  <RadioGroupItem value="payment" id="export-type-payment" />
                  <div>
                    <span className="text-sm font-medium">Payment</span>
                    <p className="text-xs text-muted-foreground">Lender / Astrafin payment details</p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={exporting}>
              Cancel
            </Button>
            <Button className="gap-2" onClick={() => void handleExport()} disabled={exporting}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

type InvoiceTabFilter = "all" | "due" | "pending" | "partially-paid" | "overdue" | "paid";
type InvoiceTabCounts = { all: number; due: number; pending: number; "partially-paid": number; overdue: number; paid: number };
type InvoiceKpis = { total: number; billed: number; collected: number; outstanding: number; uniqueSchedules: number; overdueCount: number; overdueAmt: number; monthlyBilled: number };

const ITEMS_PER_PAGE = 200;

const currency = (n: number) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** Compact INR: K (thousands), L (lakhs), Cr (crore); full amount below ₹1,000. */
const lakhsLabel = (n: number) => {
  const value = Number(n || 0);
  const abs = Math.abs(value);
  if (abs >= 10_000_000) {
    return `₹${parseFloat((value / 10_000_000).toFixed(2))}Cr`;
  }
  if (abs >= 100_000) {
    return `₹${parseFloat((value / 100_000).toFixed(2))}L`;
  }
  if (abs >= 1_000) {
    return `₹${parseFloat((value / 1_000).toFixed(2))}K`;
  }
  return currency(value);
};

function normalizedCustomerId(customerId?: string): string | undefined {
  const id = customerId != null ? String(customerId).trim() : "";
  return id ? id : undefined;
}

const LenderAvatar = memo(function LenderAvatar({
  name,
  logoUrl,
  fallbackClassName,
}: {
  name: string;
  logoUrl?: string;
  fallbackClassName: string;
}) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [logoUrl]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 min-w-0">
            <Avatar className="rounded-lg shrink-0" style={{ height: "50px", width: "80px" }}>
              {logoUrl ? (
                <AvatarImage
                  src={logoUrl}
                  alt={name}
                  className={`object-contain ${loaded ? "opacity-100" : "opacity-0"} transition-opacity duration-150`}
                  onLoad={() => setLoaded(true)}
                  onError={() => setLoaded(false)}
                />
              ) : null}
              {(!logoUrl || !loaded) && (
                <AvatarFallback className={fallbackClassName}>{name ? name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() : "?"}</AvatarFallback>
              )}
            </Avatar>
            <span className="font-medium truncate max-w-[140px]">{name}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>{name}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

/** API may return strings or rows like `{ lender: "..." }` */
function parseUniqueLenderRows(rows: unknown[]): string[] {
  const out: string[] = [];
  for (const row of rows) {
    if (typeof row === "string" && row.trim()) {
      out.push(row.trim());
      continue;
    }
    if (row && typeof row === "object") {
      const o = row as Record<string, unknown>;
      const raw = o.lender ?? o.lenderName ?? o.name ?? o.lenderCompanyName;
      if (typeof raw === "string" && raw.trim()) out.push(raw.trim());
    }
  }
  return [...new Set(out)];
}

interface InvoiceProps {
  customerId?: string;
}

export default function Invoices({ customerId }: InvoiceProps) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<invoiceTableData[]>([]);

  // UI state — default to "due" (Due This Month)
  const [tabFilter, setTabFilter] = useState<InvoiceTabFilter>("due");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [lenderFilter, setLenderFilter] = useState("all");
  const [scheduleFilter, setScheduleFilter] = useState("all");

  // Drawers
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | undefined>();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedScheduleId] = useState<string | undefined>();
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [indiaState, setIndiaState] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(200);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | undefined>();
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
  const [uniqueLenderNames, setUniqueLenderNames] = useState<string[]>([]);
  const [apiTabCounts, setApiTabCounts] = useState<InvoiceTabCounts>({ all: 0, due: 0, pending: 0, "partially-paid": 0, overdue: 0, paid: 0 });
  const [apiKpis, setApiKpis] = useState<InvoiceKpis>({ total: 0, billed: 0, collected: 0, outstanding: 0, uniqueSchedules: 0, overdueCount: 0, overdueAmt: 0, monthlyBilled: 0 });
  /** From GET get-rental-schedule-invoice-unique-schedules/:customerId */
  const [scheduleFilterOptions, setScheduleFilterOptions] = useState<Array<{ scheduleId: string; rentalScheduleDisplayId: string }>>([]);
  /** From GET /rental-schedule/noa-customers-and-lenders — customers only */
  const [companyList, setCompanyList] = useState<Array<{ id: string; name: string }>>([]);

  const routeCustomerId = normalizedCustomerId(customerId);
  const showCustomerPicker = !routeCustomerId;

  // Fetch invoices when page / filters change
  useEffect(() => {
    fetchInvoices();
  }, [currentPage, customerId, tabFilter, customerFilter, lenderFilter, scheduleFilter]);

  // Stats + lender list + unique schedules: once per customer (or global when no customerId)
  useEffect(() => {
    void fetchInvoiceStats();
    void fetchUniqueLenderName();
    void fetchUniqueSchedules();
  }, [customerId]);

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (routeCustomerId) {
      setCustomerFilter("all");
    }
  }, [routeCustomerId]);

  useEffect(() => {
    if (!showCustomerPicker) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiService.get<{ data?: unknown } | unknown>(`/rental-schedule/get-noa-customers`);
        const payload = (response as { data?: unknown })?.data ?? response;
        const raw = Array.isArray(payload)
          ? payload
          : Array.isArray((payload as { customers?: unknown[] })?.customers)
            ? (payload as { customers: unknown[] }).customers
            : [];
        const mapped: Array<{ id: string; name: string }> = [];
        for (const row of raw) {
          if (!row || typeof row !== "object") continue;
          const r = row as Record<string, unknown>;
          const id = String(r.id ?? r.customerId ?? "").trim();
          const name = String(r.name ?? r.company_name ?? "").trim();
          if (id && name) mapped.push({ id, name });
        }
        mapped.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (!cancelled) setCompanyList(mapped);
      } catch (e) {
        console.error("Error fetching company list:", e);
        if (!cancelled) setCompanyList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCustomerPicker]);

  const fetchStates = async () => {
    const response = await apiService.get<any>(`/supplier/get-india-state`);
    setIndiaState(response.data);
  };


  const fetchInvoices = async () => {
    const cid = routeCustomerId ;
    const listBase = cid
      ? `/rental-schedule/get-rental-schedule-invoices/${cid}`
      : `/rental-schedule/get-rental-schedule-invoices`;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("page", String(currentPage));
      params.set("pageSize", String(pageSize));
      if (tabFilter !== "all") params.set("tab", tabFilter);
      // When route has no customer, filter by selected company via query param
      if (!cid && customerFilter !== "all") params.set("customerId", customerFilter);
      if (lenderFilter !== "all") params.set("lenderName", lenderFilter);
      if (scheduleFilter !== "all") params.set("scheduleId", scheduleFilter);

      const response = await apiService.get<{
        status?: number;
        message?: string;
        data?: Invoice[];
        counts?: Partial<InvoiceTabCounts>;
        tabCounts?: Partial<InvoiceTabCounts>;
        kpis?: Partial<InvoiceKpis>;
        summary?: Partial<InvoiceKpis>;
        pagination?: {
          currentPage: number;
          pageSize: number;
          totalInvoices: number;
          totalPages: number;
          hasNextPage: boolean;
          hasPreviousPage: boolean;
        };
      }>(
        `${listBase}?${params.toString()}`
      );

      // Handle paginated response format
      if (response && response.data && Array.isArray(response.data)) {
        setInvoices(response.data as unknown as invoiceTableData[]);
        
        // Update pagination metadata
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalInvoices(response.pagination.totalInvoices);
        }
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response
        setInvoices(response as unknown as invoiceTableData[]);
        setTotalInvoices(response.length);
        setTotalPages(Math.ceil(response.length / pageSize));
      } else {
        setInvoices([]);
        setTotalInvoices(0);
        setTotalPages(1);
        setApiTabCounts({ all: 0, due: 0, pending: 0, "partially-paid": 0, overdue: 0, paid: 0 });
        setApiKpis({ total: 0, billed: 0, collected: 0, outstanding: 0, uniqueSchedules: 0, overdueCount: 0, overdueAmt: 0, monthlyBilled: 0 });
      }
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast({
        title: "Error",
        description: "Failed to fetch invoices",
        variant: "destructive",
      });
      setInvoices([]);
      setTotalInvoices(0);
      setTotalPages(1);
      setApiTabCounts({ all: 0, due: 0, pending: 0, "partially-paid": 0, overdue: 0, paid: 0 });
      setApiKpis({ total: 0, billed: 0, collected: 0, outstanding: 0, uniqueSchedules: 0, overdueCount: 0, overdueAmt: 0, monthlyBilled: 0 });
      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceStats = async () => {
    const cid = normalizedCustomerId(customerId);
    const statsPath = cid
      ? `/rental-schedule/get-rental-schedule-invoice-stats/${cid}`
      : `/rental-schedule/get-rental-schedule-invoice-stats`;
    try {
      const response = await apiService.get<{
        data?: {
          tabCounts?: Partial<InvoiceTabCounts>;
          kpis?: Partial<InvoiceKpis>;
        };
      }>(statsPath);

      const payload = response?.data;
      const tabCountsPayload = payload?.tabCounts;
      const kpisPayload = payload?.kpis;

      setApiTabCounts({
        all: Number(tabCountsPayload?.all ?? 0),
        due: Number(tabCountsPayload?.due ?? 0),
        pending: Number(tabCountsPayload?.pending ?? 0),
        "partially-paid": Number(tabCountsPayload?.["partially-paid"] ?? 0),
        overdue: Number(tabCountsPayload?.overdue ?? 0),
        paid: Number(tabCountsPayload?.paid ?? 0),
      });

      setApiKpis({
        total: Number(kpisPayload?.total ?? 0),
        billed: Number(kpisPayload?.billed ?? 0),
        monthlyBilled: Number(kpisPayload?.monthlyBilled ?? 0),
        collected: Number(kpisPayload?.collected ?? 0),
        outstanding: Number(kpisPayload?.outstanding ?? 0),
        uniqueSchedules: Number(kpisPayload?.uniqueSchedules ?? 0),
        overdueCount: Number(kpisPayload?.overdueCount ?? 0),
        overdueAmt: Number(kpisPayload?.overdueAmt ?? 0),
      });
    } catch (error) {
      console.error("Error fetching invoice stats:", error);
      setApiTabCounts({ all: 0, due: 0, pending: 0, "partially-paid": 0, overdue: 0, paid: 0 });
      setApiKpis({ total: 0, billed: 0, collected: 0, outstanding: 0, uniqueSchedules: 0, overdueCount: 0, overdueAmt: 0, monthlyBilled: 0 });
    }
  };

  const  fetchUniqueLenderName  = async () => {
    const cid = normalizedCustomerId(customerId);
    const lenderPath = cid
      ? `/rental-schedule/get-unique-lender-name/${cid}`
      : `/rental-schedule/get-unique-lender-name`;
    try {
      // setLoading(true);
      const response:any = await apiService.get<{ data?: any } | any>(lenderPath);  

      if (response.data && Array.isArray(response.data)) {
        setUniqueLenderNames(parseUniqueLenderRows(response.data));
      } else if (Array.isArray(response)) {
        setUniqueLenderNames(parseUniqueLenderRows(response));
      }
    } catch (error) {
      console.error("Error fetching unique lender names:", error);
      toast({
        title: "Error",
        description: "Failed to fetch unique lender names",
        variant: "destructive",
      });
      setUniqueLenderNames([]);
    } 
  };

  const fetchUniqueSchedules = async () => {
    if(!customerId) return;
    const cid = normalizedCustomerId(customerId);
    const schedulesPath = cid
      ? `/rental-schedule/get-rental-schedule-invoice-unique-schedules/${cid}`
      : `/rental-schedule/get-rental-schedule-invoice-unique-schedules`;
    try {
      const response = await apiService.get<{ data?: unknown } | unknown>(schedulesPath);
      const root = response as Record<string, unknown>;
      const raw = root?.data ?? response;
      const list: unknown[] = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { schedules?: unknown[] })?.schedules)
          ? (raw as { schedules: unknown[] }).schedules
          : [];
      const mapped = list
        .map((item: unknown) => {
          const r = item as Record<string, unknown>;
          const scheduleId = String(r?.scheduleId ?? r?.schedule_id ?? "").trim();
          const rentalScheduleDisplayId = String(
            r?.rentalScheduleDisplayId ?? r?.rental_schedule_display_id ?? r?.displayId ?? scheduleId
          ).trim();
          return { scheduleId, rentalScheduleDisplayId: rentalScheduleDisplayId || scheduleId };
        })
        .filter((s) => s.scheduleId);
      setScheduleFilterOptions(mapped);
    } catch (error) {
      console.error("Error fetching unique schedules:", error);
      setScheduleFilterOptions([]);
    }
  };

  const fetchInvoiceDetail = async (invoiceId: string) => {
    const res = await apiService.get<{ data?: Invoice } | Invoice>(`/rental-schedule/get-rental-schedule-invoice/${invoiceId}`);
    const detail = (res as { data?: Invoice })?.data ?? (res as Invoice);
    if (detail && typeof detail === "object" && "invoiceId" in detail) {
      const raw = detail as Invoice & {
        collection_route?: unknown;
        assignment_status?: unknown;
        assigned_status?: unknown;
      };
      const assignedStatus =
        (raw.assignedStatus ??
          raw.assigned_status ??
          raw.assignment_status) as Invoice["assignedStatus"];
      return {
        ...raw,
        collectionRoute:
          normalizeCollectionRoute(raw.collectionRoute ?? raw.collection_route) ?? raw.collectionRoute,
        assignedStatus: assignedStatus ?? null,
      } as Invoice;
    }
    return undefined;
  };

  const handleExportExcel = async () => {
    const cid = routeCustomerId;
    const exportBase = cid
      ? `/rental-schedule/export-rental-schedule-invoices/${cid}`
      : `/rental-schedule/export-rental-schedule-invoices`;

    try {
      const params = new URLSearchParams();
      // Export is always for Due tab only; ignore UI filters for customer/lender/schedule
      params.set("tab", "due");

      const blob = await apiService.get<Blob>(`${exportBase}?${params.toString()}`, {
        responseType: "blob",
      });

      const fileName = `invoice-summary-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting invoice excel:", error);
      toast({
        title: "Error",
        description: "Failed to export invoice report",
        variant: "destructive",
      });
    }
  };

  const handleViewInvoice = async (invoiceId: string) => {
    if (!invoiceId) return;
    setSelectedInvoiceId(invoiceId);
    setSelectedInvoice(undefined);
    setDrawerOpen(true);
    setInvoiceDetailLoading(true);
    try {
      const detail = await fetchInvoiceDetail(invoiceId);
      if (detail) setSelectedInvoice(detail);
      else {
        toast({ title: "Not found", description: "Could not load invoice details.", variant: "destructive" });
        setDrawerOpen(false);
      }
    } catch (e) {
      console.error("Error fetching invoice detail:", e);
      toast({ title: "Error", description: "Failed to load invoice details", variant: "destructive" });
      setDrawerOpen(false);
    } finally {
      setInvoiceDetailLoading(false);
    }
  };

  const handleInvoiceUpdate = (updatedInvoice: Invoice) => {
    setSelectedInvoice(updatedInvoice);
    void fetchInvoices();
  };

  // Helpers
  const getLenderColor = (name?: string) => {
    if (!name) return "bg-muted";
    const colors = ["bg-gradient-to-br from-blue-500 to-blue-600", "bg-gradient-to-br from-emerald-500 to-emerald-600", "bg-gradient-to-br from-violet-500 to-violet-600", "bg-gradient-to-br from-amber-500 to-amber-600", "bg-gradient-to-br from-rose-500 to-rose-600"];
    return colors[name.charCodeAt(0) % colors.length];
  };

  const lenderList = useMemo(() => {
    const s = new Set<string>();
    uniqueLenderNames.forEach((name) => {
      if (name?.trim()) s.add(name.trim());
    });
    return Array.from(s);
  }, [uniqueLenderNames]);
  
  const tabCounts = apiTabCounts;
  const kpis = apiKpis;

  const getStatusBadge = (status: InvoiceStatus) => {
    return <Badge className={getInvoiceStatusColor(status)}>{status}</Badge>;
  };

  const getAssignedStatusChip = (value: unknown) => {
    const status = getAssignedStatus({ assignedStatus: value as Invoice["assignedStatus"] }) ?? "N";
    const label =
      status === "A" ? "Fully assigned" : status === "P" ? "Partial assigned" : "Not assigned";
    const chip =
      status === "A" ? (
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">A</Badge>
      ) : status === "P" ? (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">P</Badge>
      ) : (
        <Badge variant="outline" className="text-muted-foreground">N</Badge>
      );

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex justify-center">{chip}</span>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const getStatusBorderColor = (status: InvoiceStatus) => {
    switch (status) {
      case "Overdue": return "border-l-red-500";
      case "Paid": return "border-l-green-500";
      case "Partially Paid": return "border-l-yellow-500";
      case "Pending": return "border-l-blue-500";
      default: return "border-l-muted";
    }
  };

  const getInvoiceTableData = (invoice: invoiceTableData) => {
    // const schedule = invoice.scheduleId;
    const paid = invoice.paid;
    const outstanding = invoice.amount - paid;
    const status = getInvoiceStatus(invoice);
    const periodNo = String(invoice.periodNo);
    const scheduleDisplayIds: string[] = [];
      scheduleDisplayIds.push(`RS NO -${invoice.rentalScheduleDisplayId}`);
  
    return { ...invoice, scheduleDisplayId: invoice.rentalScheduleDisplayId || "-", scheduleDisplayIds, periodNo, paid, outstanding, status, assignedStatus: invoice.assignedStatus ?? null };
  };

  const PaginationBar = ({ current, total, count, onPage }: { current: number; total: number; count: number; onPage: (p: number) => void }) => {
    if (count <= ITEMS_PER_PAGE) return null;
    const start = (current - 1) * ITEMS_PER_PAGE + 1;
    const end = Math.min(current * ITEMS_PER_PAGE, count);
    return (
      <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/30">
        <span className="text-xs text-muted-foreground">Showing {start}–{end} of {count}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 px-2" disabled={current <= 1} onClick={() => onPage(current - 1)}>
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /><span className="text-xs">Previous</span>
          </Button>
          <span className="text-xs text-muted-foreground">Page {current} of {total}</span>
          <Button variant="outline" size="sm" className="h-7 px-2" disabled={current >= total} onClick={() => onPage(current + 1)}>
            <span className="text-xs">Next</span><ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <FileX className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-semibold mb-1">No invoices found</h3>
      <p className="text-xs text-muted-foreground max-w-[240px]">Try adjusting your filters or switch tabs to see invoices.</p>
    </div>
  );

  const toAbsoluteLenderAvatarUrl = (avatarPath?: string): string | undefined => {
    if (!avatarPath) return undefined;
    if (/^https?:\/\//i.test(avatarPath)) return avatarPath;
  
    const baseUrl = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
    const cleanPath = String(avatarPath).replace(/^\/+/, "");
    if (!baseUrl) return `/${cleanPath}`;
    return `${baseUrl}/${cleanPath}`;
  };

  const tabItems: { key: InvoiceTabFilter; label: string; icon: React.ReactNode }[] = [
    { key: "due", label: "Due This Month", icon: <Clock className="h-3.5 w-3.5" /> },
    { key: "pending", label: "Pending", icon: <Clock className="h-3.5 w-3.5" /> },
    { key: "partially-paid", label: "Partially Paid", icon: <TrendingDown className="h-3.5 w-3.5" /> },
    { key: "overdue", label: "Overdue", icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    { key: "paid", label: "Paid", icon: <CheckCircle className="h-3.5 w-3.5" /> },
    { key: "all", label: "All", icon: <FileText className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="space-y-5 w-[100%] p-2 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Invoices</h2>
        </div>
        <div className="flex items-center gap-2">
          {showCustomerPicker && (
            <>
              <InvoiceExportButton />
              <Button size="sm" onClick={() => navigate("/invoice-payment")}>
                <CreditCard className="h-4 w-4 mr-1" />
                Payment Reconciliation
              </Button>
            </>
          )}
          {routeCustomerId && (
            <>
              <Button variant="outline" size="sm" onClick={() => void handleExportExcel()}>
                <Download className="h-4 w-4 mr-1" />Export Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEmailSettingsOpen(true)}>
                <Mail className="h-4 w-4 mr-1" />Email Settings
              </Button>
            </>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroKPICard title="Total Invoices" value={kpis.total} subtitle={`Across ${kpis.uniqueSchedules} schedule${kpis.uniqueSchedules !== 1 ? 's' : ''}`} icon={<Receipt className="h-4 w-4" />} variant="info" />
        <HeroKPICard title="Monthly Billing" value={lakhsLabel(kpis.monthlyBilled)} subtitle="Monthly invoiced" icon={<IndianRupee className="h-4 w-4" />} variant="info" />

        <HeroKPICard title="Total Billed" value={lakhsLabel(kpis.billed)} subtitle="Cumulative invoiced" icon={<TrendingUp className="h-4 w-4" />} variant="default" />
        <HeroKPICard title="Collected" value={lakhsLabel(kpis.collected)} subtitle={kpis.billed > 0 ? `${Math.round((kpis.collected / kpis.billed) * 100)}% of billed` : "—"} icon={<CheckCircle className="h-4 w-4" />} variant="success" />
        <HeroKPICard title="Outstanding" value={lakhsLabel(kpis.outstanding)} subtitle={kpis.billed > 0 ? `${Math.round((kpis.outstanding / kpis.billed) * 100)}% of billed` : "—"} icon={<TrendingDown className="h-4 w-4" />} variant="warning" />
        {/* <HeroKPICard title="Overdue" value={kpis.overdueCount} subtitle={kpis.overdueCount === 0 ? "All on track" : `${kpis.overdueCount} past due`} icon={<AlertTriangle className="h-4 w-4" />} variant="danger" /> */}
        <HeroKPICard title="Overdue Amount" value={lakhsLabel(kpis.overdueAmt)}  subtitle={kpis.overdueCount === 0 ? "All on track" : `${kpis.overdueCount} past due`} icon={<Clock className="h-4 w-4" />} variant="danger" />
      </div>

      {/* Inline Filters */}
      <div className="flex items-center gap-3">
        {showCustomerPicker && (
          <SearchableSelect
            options={[{ value: "all", label: "All Customers" }, ...companyList.map((c) => ({ value: c.id, label: c.name }))]}
            value={customerFilter}
            onValueChange={(v) => {
              setCustomerFilter(v);
              setCurrentPage(1);
            }}
            placeholder="Customer"
            searchPlaceholder="Search customers..."
            className="h-8 w-[180px] text-xs"
          />
        )}
        <SearchableSelect
          options={[{ value: "all", label: "All Lenders" }, ...lenderList.map((l: string) => ({ value: l, label: l }))]}
          value={lenderFilter} onValueChange={setLenderFilter}
          placeholder="Lender" searchPlaceholder="Search lenders..."
          className="h-8 w-[160px] text-xs"
        />

        {routeCustomerId && (
        <SearchableSelect
          options={[
            { value: "all", label: "All Schedules" },
            ...[...scheduleFilterOptions]
              .sort((a, b) => {
                const aVal = Number(a.rentalScheduleDisplayId);
                const bVal = Number(b.rentalScheduleDisplayId);
                if (Number.isFinite(aVal) && Number.isFinite(bVal)) return aVal - bVal;
                return String(a.rentalScheduleDisplayId).localeCompare(String(b.rentalScheduleDisplayId), undefined, { numeric: true });
              })
              .map(({ scheduleId, rentalScheduleDisplayId }) => ({
              value: scheduleId,
              label: rentalScheduleDisplayId ? `RS No -${rentalScheduleDisplayId}` : scheduleId,
            })),
          ]}
          value={scheduleFilter} onValueChange={setScheduleFilter}
          placeholder="Schedule" searchPlaceholder="Search schedules..."
          className="h-8 w-[160px] text-xs"
        />
        )}
        {(customerFilter !== "all" || lenderFilter !== "all" || scheduleFilter !== "all") && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={() => { setCustomerFilter("all"); setLenderFilter("all"); setScheduleFilter("all"); }}>
            Clear
          </Button>
        )}
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg w-fit">
        {tabItems.map(tab => (
          <Button
            key={tab.key}
            variant={tabFilter === tab.key ? "default" : "ghost"}
            size="sm"
            className={`gap-1.5 h-8 text-xs ${tabFilter === tab.key ? "" : "text-muted-foreground"}`}
            onClick={() => { setTabFilter(tab.key); setCurrentPage(1); }}
          >
            {tab.icon}
            {tab.label}
            <span className={`ml-0.5 text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center ${tabFilter === tab.key ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              {tabCounts[tab.key]}
            </span>
          </Button>
        ))}
      </div>

      {/* Invoice Table */}
      <div className="rounded-xl border border-border/60 overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 backdrop-blur-sm">
              <TableHead className="text-xs font-semibold w-[4px] p-0"></TableHead>
              {!routeCustomerId && <TableHead className="text-xs font-semibold">Customer</TableHead>}
              <TableHead className="text-xs font-semibold">Invoice ID</TableHead>
              <TableHead className="text-xs font-semibold">Schedule</TableHead>
              <TableHead className="text-center text-xs font-semibold">Inst #</TableHead>
              <TableHead className="text-xs font-semibold">Assignment</TableHead>
              <TableHead className="text-xs font-semibold">Invoice Date</TableHead>
              <TableHead className="text-xs font-semibold">Due Date</TableHead>
              <TableHead className="text-right text-xs font-semibold">Amount</TableHead>
              <TableHead className="text-right text-xs font-semibold">Paid</TableHead>
              <TableHead className="text-center text-xs font-semibold">Status</TableHead>
              <TableHead className="text-center text-xs font-semibold">Assigned Status</TableHead>
              <TableHead className="text-center text-xs font-semibold">NACH</TableHead>
              <TableHead className="text-center text-xs font-semibold">IRN</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} className="py-10 text-center text-sm text-muted-foreground">
                  Loading invoices...
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={12}><EmptyState /></TableCell></TableRow>
            ) : (
              invoices.map((invoice) => {
                
                const data = getInvoiceTableData(invoice);
                const dd = new Date(invoice.dueDate);
                const statusBorderClass = getStatusBorderColor(data.status);
                return (
                  <TableRow key={invoice.rsInvoiceCode} className="cursor-pointer transition-colors hover:bg-muted/40" onClick={() => { void handleViewInvoice(invoice.invoiceId || ""); }}>
                    <TableCell className={`p-0 w-1 border-l-4 ${statusBorderClass}`}></TableCell>
                    {!routeCustomerId && (
                    <TableCell className="text-xs font-semibold">
                      <span className="hover:underline font-medium text-xs">
                        {invoice.customerName || "-"}
                      </span>
                    </TableCell>
                    )}
                    <TableCell className="font-mono text-xs">
                      <span className="px-2 py-1 bg-muted rounded text-foreground font-medium">{invoice.rsInvoiceCode?.slice(0, 20) || "-"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-primary-dark hover:underline font-medium text-xs">
                        {data.scheduleDisplayIds.join(", ") || `RS-${data.scheduleDisplayId}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center text-xs font-medium">{data.periodNo}</span>
                    </TableCell>
                    <TableCell>
                      {invoice.assignedToLender ? (
                        <div className="flex items-center gap-1.5">
                          <LenderAvatar
                            name={invoice?.lenderName || ""}
                            logoUrl={toAbsoluteLenderAvatarUrl(invoice?.lenderAvatar || undefined)}
                            fallbackClassName={`bg-gradient-to-br ${getLenderColor(invoice?.lenderName || "")} text-white text-[9px] font-bold`}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Avatar className="h-6 w-9 rounded shrink-0">
                            <AvatarImage
                              src={astrafinLogo}
                              alt="Astrafin"
                              className="object-contain"
                            />
                          </Avatar>
                          <span className="text-xs">Astrafin</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {invoice.invoiceDate ? format(new Date(invoice.invoiceDate), "dd MMM yyyy") : "-"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {format(dd, "dd MMM yyyy")}
                    </TableCell>
                    <TableCell className="text-right font-medium font-mono text-sm tabular-nums">{currency(invoice.amount)}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">
                      <span className={data.paid > 0 ? "text-emerald-600 font-medium" : "text-muted-foreground"}>{currency(data.paid)}</span>
                    </TableCell>
                    <TableCell className="text-center">{getStatusBadge(data.status)}</TableCell>
                    <TableCell className="text-center">{getAssignedStatusChip(data.assignedStatus)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {invoice.nach ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-sm">
                                  <CreditCard className="h-3 w-3 text-white" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>NACH Active</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : <span className="text-muted-foreground text-xs">-</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {invoice.externalAckNo ? (
                        <div className="flex items-center justify-center">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="w-7 h-7 rounded-md bg-emerald-100 flex items-center justify-center">
                                  <QrCode className="h-4 w-4 text-emerald-600" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>IRN Generated</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10" onClick={(e) => {
                            e.stopPropagation();
                            void handleViewInvoice(invoice.invoiceId || "");
                          }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {invoices.length > 0 && (
            <TableFooter>
              <TableRow className="bg-muted/80 font-semibold">
                <TableCell></TableCell>
                <TableCell colSpan={routeCustomerId ? 6 : 7} className="text-right text-sm">Total ({invoices.length} invoices)</TableCell>
                <TableCell className="text-right font-mono text-sm tabular-nums">{currency(invoices.reduce((acc, inv) => acc + inv.amount, 0))}</TableCell>
                <TableCell className="text-right font-mono text-sm text-emerald-600 tabular-nums">{currency(invoices.reduce((acc, inv) => acc + inv.paid, 0))}</TableCell>
                <TableCell colSpan={3}></TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
        <PaginationBar current={currentPage} total={totalPages} count={totalInvoices} onPage={setCurrentPage} />
      </div>

      {/* Drawers & Modals */}
      {/* <InvoiceDetailsDrawer open={drawerOpen} onOpenChange={setDrawerOpen} invoiceId={selectedInvoiceId} /> */}
      {selectedScheduleId && <InvoiceSettingsModal open={settingsModalOpen} onOpenChange={setSettingsModalOpen} scheduleId={selectedScheduleId} />}


      <InvoiceDetailsDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) {
            setSelectedInvoice(undefined);
            setSelectedInvoiceId(undefined);
            setInvoiceDetailLoading(false);
          }
        }}
        invoiceId={selectedInvoiceId}
        currentInvoice={selectedInvoice}
        onInvoiceUpdate={handleInvoiceUpdate}
        indiaState={indiaState}
        loading={invoiceDetailLoading}
      />

      {/* <ManualInvoiceGenerator open={manualInvoiceOpen} onOpenChange={setManualInvoiceOpen} onSave={handleSaveManualInvoice} /> */}
      <InvoiceDashboardEmailSettings open={emailSettingsOpen} onOpenChange={setEmailSettingsOpen} customerId={customerId || ""} />
    </div>
  );
}
